/**
 * Comprehensive demo data for the Feed component
 * Contains all data needed for trending hashtags, boosted profiles, media items, and song items
 */

import { 
  UserCheck,
  MapPin,
  Image,
  Video,
  Music,
  Globe,
} from "lucide-react";
import type { BoostedUser, MediaItem, SongItem } from '../types/index';

// Demo tabs for feed navigation
export const demoTabs = [
  { id: "all", label: "All", icon: Globe },
  { id: "following", label: "Following", icon: UserCheck },
  { id: "nearby", label: "Nearby", icon: MapPin },
  { id: "photo", label: "Photo", icon: Image },
  { id: "video", label: "Video", icon: Video },
  { id: "music", label: "Music", icon: Music },
  { id: "countries", label: "Countries", icon: Globe },
];

// Demo trending hashtags
export const demoTrendingHashtags = [
  "MoZoLive", "TechTalk", "MusicVibes", "TravelGoals",
  "FoodieLife", "FitnessJourney", "ArtisticSoul", "GamingWorld",
  "Photography", "DigitalArt", "Coding", "Adventure",
  "Wellness", "Fashion", "Nature", "Innovation"
];

// Demo boosted profiles
export const demoBoostedProfiles: BoostedUser[] = [
  {
    id: "boosted-1",
    username: "creativeminds",
    fullName: "Creative Minds",
    avatar_url: "https://cdn.pixabay.com/photo/2016/11/18/19/07/happy-1836445_1280.jpg",
    cover_url: "https://cdn.pixabay.com/photo/2016/11/23/14/45/coding-1853305_1280.jpg",
    bio: "Digital artist sharing creative work and inspiration. Join me on this artistic journey!",
    verified: true,
    followers: 15420,
    website: "creativeminds.art",
    media: [
      { id: "cm-1", url: "https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_1280.jpg", type: "image" as const },
      { id: "cm-2", url: "https://cdn.pixabay.com/photo/2017/02/08/17/24/fantasy-2049567_1280.jpg", type: "image" as const },
      { id: "cm-3", url: "https://cdn.pixabay.com/photo/2015/12/01/20/28/road-1072823_1280.jpg", type: "image" as const },
    ],
  },
  {
    id: "boosted-2",
    username: "travelbug",
    fullName: "Travel Bug Adventures",
    avatar_url: "https://cdn.pixabay.com/photo/2016/11/29/13/25/attractive-1869761_1280.jpg",
    cover_url: "https://cdn.pixabay.com/photo/2016/01/09/18/27/old-1130731_1280.jpg",
    bio: "Exploring the world one adventure at a time. Travel tips, photos, and stories!",
    verified: true,
    followers: 28750,
    website: "travelbugtips.com",
    media: [
      { id: "tb-1", url: "https://cdn.pixabay.com/photo/2017/01/14/13/59/castelmezzano-1979546_1280.jpg", type: "image" as const },
      { id: "tb-2", url: "https://cdn.pixabay.com/photo/2016/11/21/16/59/beach-1846597_1280.jpg", type: "image" as const },
      { id: "tb-3", url: "https://cdn.pixabay.com/photo/2016/11/13/12/52/kuala-lumpur-1820944_1280.jpg", type: "image" as const },
    ],
  },
  {
    id: "boosted-3",
    username: "musicproducer",
    fullName: "Music Producer",
    avatar_url: "https://cdn.pixabay.com/photo/2016/11/29/06/46/adult-1867889_1280.jpg",
    cover_url: "https://cdn.pixabay.com/photo/2015/05/15/14/50/concert-768722_1280.jpg",
    bio: "Music producer and DJ sharing latest tracks and mixes. Follow for new releases!",
    verified: true,
    followers: 42680,
    website: "musicproducer.beats",
    media: [
      { id: "mp-1", url: "https://cdn.pixabay.com/photo/2015/04/23/22/00/tree-736885_1280.jpg", type: "image" as const },
      { id: "mp-2", url: "https://cdn.pixabay.com/photo/2015/12/01/20/28/road-1072823_1280.jpg", type: "image" as const },
      { id: "mp-3", url: "https://cdn.pixabay.com/photo/2015/06/19/21/24/avenue-815297_1280.jpg", type: "image" as const },
    ],
  },
  {
    id: "boosted-4",
    username: "techguru",
    fullName: "Tech Guru",
    avatar_url: "https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg",
    cover_url: "https://cdn.pixabay.com/photo/2016/11/19/14/00/code-1839877_1280.jpg",
    bio: "Technology enthusiast sharing the latest in AI, blockchain, and innovation!",
    verified: true,
    followers: 38920,
    website: "techguru.dev",
    media: [
      { id: "tg-1", url: "https://cdn.pixabay.com/photo/2016/11/19/14/00/code-1839877_1280.jpg", type: "image" as const },
      { id: "tg-2", url: "https://cdn.pixabay.com/photo/2017/05/10/19/29/robot-2301646_1280.jpg", type: "image" as const },
      { id: "tg-3", url: "https://cdn.pixabay.com/photo/2016/11/30/20/58/programming-1873854_1280.jpg", type: "image" as const },
    ],
  },
  {
    id: "boosted-5",
    username: "foodiechef",
    fullName: "Foodie Chef",
    avatar_url: "https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg",
    cover_url: "https://cdn.pixabay.com/photo/2017/07/16/10/43/recipe-2508859_1280.jpg",
    bio: "Professional chef sharing delicious recipes and cooking tips. Bon appétit!",
    verified: true,
    followers: 52340,
    website: "foodiechef.recipes",
    media: [
      { id: "fc-1", url: "https://cdn.pixabay.com/photo/2017/07/16/10/43/recipe-2508859_1280.jpg", type: "image" as const },
      { id: "fc-2", url: "https://cdn.pixabay.com/photo/2016/11/06/23/31/breakfast-1804457_1280.jpg", type: "image" as const },
      { id: "fc-3", url: "https://cdn.pixabay.com/photo/2017/05/07/08/56/pancakes-2291908_1280.jpg", type: "image" as const },
    ],
  },
];

// Demo media items
export const demoMediaItems: MediaItem[] = [
  { id: "media-1", url: "https://cdn.pixabay.com/photo/2016/11/14/04/45/elephant-1822636_1280.jpg", type: "image" as const },
  { id: "media-2", url: "https://cdn.pixabay.com/video/2019/06/26/24077-346043893_large.mp4", type: "video" as const },
  { id: "media-3", url: "https://cdn.pixabay.com/photo/2016/11/08/05/26/woman-1807533_1280.jpg", type: "image" as const },
  { id: "media-4", url: "https://cdn.pixabay.com/photo/2019/04/07/10/34/temple-4109306_1280.jpg", type: "image" as const },
  { id: "media-5", url: "https://cdn.pixabay.com/photo/2017/08/30/01/05/milky-way-2695569_1280.jpg", type: "image" as const },
  { id: "media-6", url: "https://cdn.pixabay.com/video/2019/07/15/24919-349618123_large.mp4", type: "video" as const },
  { id: "media-7", url: "https://cdn.pixabay.com/photo/2017/02/08/17/24/fantasy-2049567_1280.jpg", type: "image" as const },
  { id: "media-8", url: "https://cdn.pixabay.com/photo/2015/12/01/20/28/road-1072823_1280.jpg", type: "image" as const },
  { id: "media-9", url: "https://cdn.pixabay.com/photo/2016/11/21/16/59/beach-1846597_1280.jpg", type: "image" as const },
  { id: "media-10", url: "https://cdn.pixabay.com/video/2020/03/26/35721-402047516_large.mp4", type: "video" as const },
];

// Demo song items
export const demoSongItems: SongItem[] = [
  {
    id: "song-1",
    url: "https://cdn.pixabay.com/audio/2022/11/13/audio_cb4f1212a9.mp3",
    title: "Summer Breeze",
    artist: "Coma Media",
    cover_image: "https://cdn.pixabay.com/photo/2017/11/11/23/42/palm-trees-2940213_1280.jpg",
    duration: "2:15",
  },
  {
    id: "song-2",
    url: "https://cdn.pixabay.com/audio/2022/05/27/audio_1808fbf07a.mp3",
    title: "Jazzy Vibes",
    artist: "FASSounds",
    cover_image: "https://cdn.pixabay.com/photo/2017/08/06/12/06/people-2591874_1280.jpg",
    duration: "3:02",
  },
  {
    id: "song-3",
    url: "https://cdn.pixabay.com/audio/2023/08/14/audio_7b8d48303f.mp3",
    title: "Tropical House",
    artist: "Music Unlimited",
    cover_image: "https://cdn.pixabay.com/photo/2016/11/19/09/57/dj-1838060_1280.jpg",
    duration: "2:48",
  },
  {
    id: "song-4",
    url: "https://cdn.pixabay.com/audio/2022/03/10/audio_d1456ab4b7.mp3",
    title: "Chill Beats",
    artist: "Audio Studio",
    cover_image: "https://cdn.pixabay.com/photo/2015/05/15/14/50/concert-768722_1280.jpg",
    duration: "2:33",
  },
  {
    id: "song-5",
    url: "https://cdn.pixabay.com/audio/2022/08/02/audio_884fe92c21.mp3",
    title: "Electronic Dreams",
    artist: "Digital Sounds",
    cover_image: "https://cdn.pixabay.com/photo/2016/11/23/15/48/audience-1853662_1280.jpg",
    duration: "3:18",
  },
  {
    id: "song-6",
    url: "https://cdn.pixabay.com/audio/2023/02/28/audio_65008d4cc7.mp3",
    title: "Acoustic Melody",
    artist: "Indie Folk",
    cover_image: "https://cdn.pixabay.com/photo/2015/05/07/11/02/guitar-756326_1280.jpg",
    duration: "2:45",
  },
];

// Demo video posts for VideosCarousel
export const demoVideoPostsData = [
  {
    id: 'carousel-video-1',
    user_id: 'user_vid_car_1',
    content_id: 'content_vid_car_1',
    caption: 'Epic Mountain Adventure',
    media_url: 'https://cdn.pixabay.com/vimeo/330195222/waterfall-24894.mp4',
    likes: 156,
    views: 1240,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 3).toISOString(),
    users: {
      id: 'user_vid_car_author_1',
      username: 'mountain_explorer',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/12/42/beard-1845166_1280.jpg',
      is_verified: true,
      verified: true,
      is_followed: false,
    },
    comments: [],
  },
  {
    id: 'carousel-video-2',
    user_id: 'user_vid_car_2',
    content_id: 'content_vid_car_2',
    caption: 'Ocean Waves Relaxation',
    media_url: 'https://cdn.pixabay.com/vimeo/330195222/ocean-24895.mp4',
    likes: 89,
    views: 750,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 5).toISOString(),
    users: {
      id: 'user_vid_car_author_2',
      username: 'ocean_vibes',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/09/38/adult-1868750_1280.jpg',
      is_verified: false,
      verified: false,
      is_followed: true,
    },
    comments: [],
  },
  {
    id: 'carousel-video-3',
    user_id: 'user_vid_car_3',
    content_id: 'content_vid_car_3',
    caption: 'City Life Timelapse',
    media_url: 'https://cdn.pixabay.com/vimeo/330195222/city-24896.mp4',
    likes: 234,
    views: 1890,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 8).toISOString(),
    users: {
      id: 'user_vid_car_author_3',
      username: 'urban_photographer',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/22/19/25/adult-1850177_1280.jpg',
      is_verified: true,
      verified: true,
      is_followed: false,
    },
    comments: [],
  },
  {
    id: 'carousel-video-4',
    user_id: 'user_vid_car_4',
    content_id: 'content_vid_car_4',
    caption: 'Wildlife Documentary',
    media_url: 'https://cdn.pixabay.com/vimeo/330195222/wildlife-24897.mp4',
    likes: 312,
    views: 2340,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 12).toISOString(),
    users: {
      id: 'user_vid_car_author_4',
      username: 'wildlife_docs',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/21/14/53/man-1845814_1280.jpg',
      is_verified: true,
      verified: true,
      is_followed: false,
    },
    comments: [],
  },
  {
    id: 'carousel-video-5',
    user_id: 'user_vid_car_5',
    content_id: 'content_vid_car_5',
    caption: 'Sunset Beach Vibes',
    media_url: 'https://cdn.pixabay.com/vimeo/330195222/beach-24898.mp4',
    likes: 445,
    views: 3120,
    type: 'video' as const,
    created_at: new Date(Date.now() - 1000 * 60 * 60 * 16).toISOString(),
    updated_at: new Date(Date.now() - 1000 * 60 * 60 * 16).toISOString(),
    users: {
      id: 'user_vid_car_author_5',
      username: 'sunset_chaser',
      avatar_url: 'https://cdn.pixabay.com/photo/2016/11/29/20/26/girl-1871104_1280.jpg',
      is_verified: false,
      verified: false,
      is_followed: true,
    },
    comments: [],
  },
];

// Demo story items for StoriesCarousel
export const demoStoryItems = [
  {
    id: 'story-1',
    title: 'The Lost City of Eldoria',
    author: 'AI Storyteller',
    cover_image: 'https://cdn.pixabay.com/photo/2017/02/08/17/24/fantasy-2049567_1280.jpg',
    excerpt: 'A thrilling adventure through mystical lands...',
    category: 'Fantasy',
    read_time: '5 min read',
    likes: 1240,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 2).toISOString(),
  },
  {
    id: 'story-2',
    title: 'Chronicles of the Starship Voyager',
    author: 'SciFi Writer Bot',
    cover_image: 'https://cdn.pixabay.com/photo/2016/11/29/05/45/astronomy-1867616_1280.jpg',
    excerpt: 'Journey through the cosmos in this epic space saga...',
    category: 'Science Fiction',
    read_time: '8 min read',
    likes: 890,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 1).toISOString(),
  },
  {
    id: 'story-3',
    title: 'Secrets of the Enchanted Forest',
    author: 'Fantasy Weaver AI',
    cover_image: 'https://cdn.pixabay.com/photo/2015/06/19/21/24/avenue-815297_1280.jpg',
    excerpt: 'Discover the magic hidden within ancient woods...',
    category: 'Fantasy',
    read_time: '6 min read',
    likes: 1560,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 3).toISOString(),
  },
  {
    id: 'story-4',
    title: 'A Detective\'s Tale: The Crimson Case',
    author: 'Mystery Bot',
    cover_image: 'https://cdn.pixabay.com/photo/2016/11/22/19/25/adult-1850177_1280.jpg',
    excerpt: 'Unravel the mystery in this gripping detective story...',
    category: 'Mystery',
    read_time: '7 min read',
    likes: 720,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 4).toISOString(),
  },
  {
    id: 'story-5',
    title: 'The Digital Revolution',
    author: 'Tech Visionary',
    cover_image: 'https://cdn.pixabay.com/photo/2016/11/19/14/00/code-1839877_1280.jpg',
    excerpt: 'How technology is reshaping our world...',
    category: 'Technology',
    read_time: '4 min read',
    likes: 980,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
  },
  {
    id: 'story-6',
    title: 'Culinary Adventures Around the World',
    author: 'Global Chef',
    cover_image: 'https://cdn.pixabay.com/photo/2017/07/16/10/43/recipe-2508859_1280.jpg',
    excerpt: 'Taste the flavors of different cultures...',
    category: 'Food & Travel',
    read_time: '6 min read',
    likes: 1340,
    published_at: new Date(Date.now() - 1000 * 60 * 60 * 24 * 6).toISOString(),
  }
];

// Export all demo data as a single object for easy importing
export const demoFeedData = {
  tabs: demoTabs,
  trendingHashtags: demoTrendingHashtags,
  boostedProfiles: demoBoostedProfiles,
  sampleMediaItems: demoMediaItems,
  sampleSongItems: demoSongItems,
  sampleVideoPostsData: demoVideoPostsData,
  sampleStoryItems: demoStoryItems,
};

export default demoFeedData;
